'use client'
/**
 * Choix de la langue par drapeaux (demande de Guillaume du 09/10/2026 :
 * « le drapeau français et anglais, touche colorée facilement identifiable »).
 *
 * Les deux drapeaux sont toujours affichés : celui de la langue en cours est
 * marqué (anneau, pleine opacité, aria-current) et n'est pas un lien ; l'autre
 * mène à la même page dans l'autre langue. Drapeaux en SVG en ligne (pas
 * d'émoji : rendu identique partout, y compris sous Windows).
 */
import { useId } from 'react'

export function DrapeauFrance({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 3 2" className={className} aria-hidden="true" focusable="false">
      <rect width="1" height="2" x="0" fill="#002395" />
      <rect width="1" height="2" x="1" fill="#FFFFFF" />
      <rect width="1" height="2" x="2" fill="#ED2939" />
    </svg>
  )
}

export function DrapeauRoyaumeUni({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  return (
    <svg viewBox="0 0 60 40" className={className} aria-hidden="true" focusable="false" preserveAspectRatio="xMidYMid slice">
      <clipPath id={`${id}-s`}>
        <path d="M0,0 v40 h60 v-40 z" />
      </clipPath>
      <clipPath id={`${id}-t`}>
        <path d="M30,20 h30 v20 z v20 h-30 z h-30 v-20 z v-20 h30 z" />
      </clipPath>
      <g clipPath={`url(#${id}-s)`}>
        <path d="M0,0 v40 h60 v-40 z" fill="#012169" />
        <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
        <path d="M0,0 L60,40 M60,0 L0,40" clipPath={`url(#${id}-t)`} stroke="#C8102E" strokeWidth="5" />
        <path d="M30,0 v40 M0,20 h60" stroke="#FFFFFF" strokeWidth="13" />
        <path d="M30,0 v40 M0,20 h60" stroke="#C8102E" strokeWidth="8" />
      </g>
    </svg>
  )
}

type Props = {
  locale: 'fr' | 'en'
  /** Adresse de la même page dans l'autre langue. */
  hrefAutre: string
  onBasculer?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  /** Fond sombre (en-tête transparent sur photo) ou clair. */
  surSombre?: boolean
  /** Libellés écrits à côté des drapeaux (menu mobile). */
  avecLibelles?: boolean
}

export default function DrapeauxLangue({ locale, hrefAutre, onBasculer, surSombre = false, avecLibelles = false }: Props) {
  const langues = [
    { code: 'fr' as const, court: 'FR', long: 'Français', aria: 'Passer en français', Drapeau: DrapeauFrance },
    { code: 'en' as const, court: 'EN', long: 'English', aria: 'Switch to English', Drapeau: DrapeauRoyaumeUni },
  ]
  const bordure = surSombre ? 'border-white/30' : 'border-gray-200'
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full border p-1 ${bordure}`}
      role="group"
      aria-label={locale === 'en' ? 'Language' : 'Langue'}
      data-testid="choix-langue"
    >
      {langues.map(({ code, court, long, aria, Drapeau }) => {
        const actif = code === locale
        const contenu = (
          <>
            <span
              className={`block overflow-hidden rounded-[3px] shadow-sm ring-1 ${
                actif ? (surSombre ? 'ring-white' : 'ring-forest') : 'ring-black/10'
              } ${avecLibelles ? 'h-4 w-6' : 'h-[14px] w-[21px]'}`}
            >
              <Drapeau className="block h-full w-full" />
            </span>
            <span
              className={`${avecLibelles ? 'text-sm' : 'text-[11px]'} font-semibold tracking-wide ${
                surSombre ? 'text-white' : actif ? 'text-forest' : 'text-gray-600'
              }`}
            >
              {avecLibelles ? long : court}
            </span>
          </>
        )
        const classes = `flex items-center gap-1.5 rounded-full ${avecLibelles ? 'px-3 py-1.5' : 'px-2 py-1'}`
        return actif ? (
          <span
            key={code}
            aria-current="true"
            className={`${classes} ${surSombre ? 'bg-white/15' : 'bg-sage/60'}`}
            title={long}
          >
            {contenu}
          </span>
        ) : (
          <a
            key={code}
            href={hrefAutre}
            onClick={onBasculer}
            hrefLang={code}
            lang={code}
            aria-label={aria}
            title={aria}
            className={`${classes} opacity-75 transition-all duration-200 hover:opacity-100 ${
              surSombre ? 'hover:bg-white/10' : 'hover:bg-sage/50'
            }`}
          >
            {contenu}
          </a>
        )
      })}
    </div>
  )
}
